/**
 * PageFooter (slim bar) — `src/Components/Footer.tsx` in v2.
 *
 * Layout: LogoMark (mono) · 4 secondary nav · copyright · 3 socials
 *
 * Used on every page. Replaces the v1 "big contact CTA footer" which has
 * moved to the dedicated `/contact` page in v2 (organism: ContactCTA).
 * See `design/HANDOFF.md` § "Footer also has a secondary nav".
 */
import React from "react";
import { Github, Linkedin, Twitter } from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { SECONDARY_NAV } from "../config/navigation";
import { LogoMark } from "../ui/molecules/LogoMark";
import { SocialLink } from "../ui/molecules/SocialLink";

const currentYear = new Date().getFullYear();

const Footer: React.FC = () => {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border-subtle bg-surface-elevated">
      <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
        {/* Left: LogoMark (mono) */}
        <LogoMark size="sm" />

        {/* Center: Secondary nav */}
        <nav
          aria-label={t("nav.more", { defaultValue: "More" })}
          className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center"
        >
          {SECONDARY_NAV.map(({ id, href, labelKey }) => (
            <Link
              key={id}
              to={href}
              className="text-[13px] font-medium text-secondary hover:text-primary transition-colors"
            >
              {t(labelKey, { defaultValue: id })}
            </Link>
          ))}
        </nav>

        {/* Right: copyright + socials */}
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
          <span className="font-mono text-xs text-tertiary">
            © {currentYear} {" // "} kevinjlope
          </span>
          <div className="flex gap-2">
            <SocialLink
              href="https://github.com/kevinjlope"
              icon={<Github size={16} />}
              label="GitHub"
              variant="default"
            />
            <SocialLink
              href="https://linkedin.com/in/kevinjlope"
              icon={<Linkedin size={16} />}
              label="LinkedIn"
              variant="default"
            />
            <SocialLink
              href="https://twitter.com/kevinjlope"
              icon={<Twitter size={16} />}
              label="Twitter"
              variant="default"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
